import {
    BreachEvent,
    createRegisterableComponent,
    DataLayerEventName,
    useFeature,
} from '@news-mono/web-common'
import { TheWestSections } from '@west-australian-newspapers/publication-types'
import React, { useEffect, useState } from 'react'
import { MessageBanner } from '../../banners/MessageBanner/MessageBanner'
import { useFeatureForSection } from '../../feature-toggling'
import { packageOfferDetails, useUpdateBreachWallContext } from './common'
import { BreachWallContent } from './components/BreachNotificationWrapper/BreachNotification/BreachWallContent/BreachWallContent'
import {
    StyledABTestBannerWrap,
    StyledMessage,
    StyledTerms,
    StyledTitle,
} from './components/BreachNotificationWrapper/BreachNotification/BreachWallContent/BreachWallContent.styled'
import { WestRewardsMessage } from './components/BreachNotificationWrapper/shared-components/WestRewardsMessage/WestRewardsMessage'
import { BreachWallContentType, BreachWallStyle } from './helpers/BreachWallTypes'
import { getAbTestTitle } from './components/BreachNotificationWrapper/BreachNotification/BreachNotification'
import { useActiveExperiment, useVariantId } from './abtesting/AbTestingProvider'
import { useSection } from '../../__helpers'
import { TheWestSection } from '@news-mono/common'
import { useLocation } from 'react-router'

export interface BreachScreenRegistrationProps {
    bannerMessage?: string
    breachCreativeName: string
    breachDeal: string
    breachType: BreachWallContentType
    title: string
    message?: string
    terms: string
}

export const BreachScreenRegistration = createRegisterableComponent(
    'breachscreen',
    (props: BreachScreenRegistrationProps, services) => {
        const is50percentOffDeal =
            props.breachDeal === 'COVIDDIG' || props.breachDeal === 'GOVAN50'
        const is30percentOffDeal =
            props.breachDeal === 'BIKEDI30' || props.breachDeal === 'SCHOOL30'

        const isNewDesign2022 = useFeatureForSection({
            feature: 'tp-subscription-new-breach-package-background',
            sections: TheWestSections.default,
        })
        const matfeature = useFeature('breach-matildas-offer')
        const canShowAbTests = useFeature('breach-ab-testing')
        const [isLoading, setLoading] = useState(true)

        const [breachDeal, setBreachDeal] = useState<string>(props.breachDeal)
        const [breachType, setBreachType] = useState<BreachWallContentType>(props.breachType)
        const [breachStyle, setBreachStyle] = useState<BreachWallStyle>('default-breachwall')
        const [experimentId, setExperimentId] = useState<string>()

        const section = useSection<TheWestSection>()
        const location = useLocation()

        const activeExperiment = useActiveExperiment(section, location, canShowAbTests)
        const variant = useVariantId(activeExperiment)

        useEffect(() => {
            if(canShowAbTests) {
                // Experiment was found and is active! :D
                if (typeof(activeExperiment) !== 'undefined' && variant !== undefined) {
                    const breachValues = activeExperiment.getBreachWallConfig(variant)

                    setBreachDeal(breachValues.deal)
                    setBreachType(breachValues.type)
                    setExperimentId(activeExperiment.getExperimentId())
                    setBreachStyle(activeExperiment.getBreachStyle(variant))
                }
            }

            if (matfeature) {
                setBreachDeal('MATI23')
                setBreachType('matildas-offer')
                setBreachStyle('aussie')
            }

            setLoading(false)
        }, [
            canShowAbTests,
            setLoading,
            activeExperiment,
            variant,
            matfeature
        ])

        const breachCreative = props.breachCreativeName;
        const isAbTest = experimentId !== undefined

        // fire breach wall context
        useUpdateBreachWallContext({
            publication: undefined,
            requiredAccessLevel: 'subscriber',
            breachDeal: breachDeal,
            breachCreativeName: breachCreative,
        })

        const eventPayload: BreachEvent['payload'] = {
            paywallType: 'freemium',
            leadDepth: 0,
            creative: breachCreative,
            deal: breachDeal ?? '',
            breachScreenType: 'Premium - Inline',
            popupMethod: 'auto',
            pricepoint: [],
        }

        const subscriptionPackages = packageOfferDetails(
            props.breachDeal === 'epaper-regional'
                ? 'digital-only'
                : breachType
                ? breachType
                : 'default',
        )

        // Fire the available event
        useEffect(() => {
            services.onEvent({
                type: DataLayerEventName.breachScreenAvailable,
                originator: 'breachscreen',
                payload: eventPayload,
            })
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [])

        return (
            <div data-testid="BreachScreen">
                {props.bannerMessage && (
                    <MessageBanner
                        icon="subscriber"
                        connector="solid-arrow"
                        onEvent={services.onEvent}
                    >
                        <p>{props.bannerMessage}</p>
                    </MessageBanner>
                )}

                {!isLoading && <BreachWallContent
                    breachCreativeName={breachCreative}
                    breachType={breachType}
                    breachDeal={breachDeal}
                    onEvent={services.onEvent}
                    title={() => {
                        if (is50percentOffDeal) {
                            return (
                                <React.Fragment>
                                    <StyledTitle>Limited Offer</StyledTitle>
                                    <StyledMessage>
                                        <p>Save over 50% for 8 weeks*</p>
                                    </StyledMessage>
                                </React.Fragment>
                            )
                        }
                        if (is30percentOffDeal) {
                            return (
                                <React.Fragment>
                                    <StyledTitle>
                                        Limited Time Offer
                                    </StyledTitle>
                                    <StyledMessage>
                                        <p>
                                            Subscribe now to save 30%* and enjoy
                                            unlimited access to investigative
                                            video series, breaking news, sport,
                                            business and lifestyle direct to
                                            your device
                                        </p>
                                    </StyledMessage>
                                </React.Fragment>
                            )
                        }
                        if (isAbTest && activeExperiment !== undefined) {
                            return getAbTestTitle(breachStyle, activeExperiment, activeExperiment?.getVariantFromType(breachType))
                        }

                        if (matfeature) {
                            return (
                                <React.Fragment>
                                    <StyledABTestBannerWrap
                                        breachStyle={breachStyle}
                                    >
                                        <StyledMessage>
                                            <StyledTitle>
                                                SCORE 50% OFF
                                            </StyledTitle>
                                            <p className="full-width">
                                                Everyday Digital for 8 weeks
                                            </p>
                                            <p className="abtest-terms">
                                                T's & C's apply
                                            </p>
                                        </StyledMessage>
                                    </StyledABTestBannerWrap>
                                </React.Fragment>
                            )
                        }

                        return (
                            <React.Fragment>
                                <StyledTitle>{props.title}</StyledTitle>
                                {props.message && (
                                    <StyledMessage>
                                        <p>{props.message}</p>
                                    </StyledMessage>
                                )}
                            </React.Fragment>
                        )
                    }}
                    terms={() => {
                        if (is50percentOffDeal || is30percentOffDeal) {
                            return (
                                <StyledTerms>
                                    *Valid for new subscribers only.
                                    {` ${breachDealTerms(props.breachDeal)}`}
                                    <br />
                                    T&Cs apply
                                </StyledTerms>
                            )
                        }

                        return null
                    }}
                    mediaBanner={() => {
                        return null
                    }}
                    subscriptionPackages={subscriptionPackages}
                />}
                {isNewDesign2022 && <WestRewardsMessage />}
            </div>
        )
    },
)

function breachDealTerms(deal: string) {
    switch (deal) {
        case 'SCHOOL30':
            return 'Offer ends 23 November 2021.'
        case 'BIKEDI30':
            return 'Offer ends 21 November 2021.'
        case 'GOVAN50':
            return 'Offer ends 20 February 2021.'
        case 'BRE1DISC':
            return 'Offer ends 2 May 2021.'
        default:
            return ''
    }
}
